import { AxiosResponse } from 'axios'
import { apiNode } from './api'
import {
	DefaultCardParamsProps,
	DefaultStatusParamsProps,
	GetBoardDataResponse,
	GetBoardPermissionsDataResponse,
	GetBoardsDataResponse,
	GetBoardStatusesDataResponse,
	GetStatusCardDataResponse,
	UpdateBoardUsersParamsProps,
	UpdateCardParamsProps,
	UpdateStatusParamsProps,
	MoveStatusDataProps,
	MoveCardDataProps,
	DefaultCardCommentDataProps,
	GetCardCommentDataResponse,
	CreateCommentPostResponse,
	DefaultCardCommentParamsProps,
	UpdateCardCommentParamsProps,
	CardComment,
	CardUser,
} from './types/board.interfaces'
import { DefaultGetParamsProps } from './types/interfaces'

const URL = '2/todo/boards'

type ApiCardUser = CardUser & {
	fullName?: string
	firstName?: string
	lastName?: string
}

const mapCardUser = (user?: ApiCardUser | null): CardUser => ({
	id: user?.id ?? '',
	name:
		user?.name ||
		user?.fullName ||
		`${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() ||
		user?.email ||
		'',
	email: user?.email ?? '',
	avatar: user?.avatar ?? '',
})

const mapCardComment = (comment: CardComment): CardComment => ({
	...comment,
	createdBy: mapCardUser(comment.createdBy),
})

export const BoardServices = {
	// boards
	async getBoards(): Promise<GetBoardsDataResponse> {
		const response: AxiosResponse<GetBoardsDataResponse> =
			await apiNode.get<GetBoardsDataResponse>(`${URL}`)
		return response.data
	},
	async createBoard({
		name,
		files,
	}: {
		name: string
		files?: string[]
	}) {
		return await apiNode.post(`${URL}/create`, { name, files })
	},
	async getBoard({
		id,
	}: DefaultGetParamsProps): Promise<GetBoardDataResponse> {
		const response: AxiosResponse<GetBoardDataResponse> =
			await apiNode.get<GetBoardDataResponse>(`${URL}/${id}`)
		return response.data
	},
	async updateBoard({
		data,
		id,
	}: {
		data: { name: string; files?: string[] }
		id: string
	}) {
		return await apiNode.patch(`${URL}/${id}/update`, data)
	},
	async deleteBoard({ id }: DefaultGetParamsProps) {
		return await apiNode.delete(`${URL}/${id}/delete`)
	},
	async getBoardPermissions({
		id,
	}: DefaultGetParamsProps): Promise<GetBoardPermissionsDataResponse> {
		const response: AxiosResponse<GetBoardPermissionsDataResponse> =
			await apiNode.get<GetBoardPermissionsDataResponse>(
				`${URL}/${id}/permissions`,
			)
		return response.data
	},
	async updateBoardPermissions({
		data,
		id,
	}: UpdateBoardUsersParamsProps) {
		return await apiNode.put(`${URL}/${id}/permissions`, data)
	},

	// statuses (boards' columns)
	async getStatusesByBoardId({
		id,
	}: DefaultGetParamsProps): Promise<GetBoardStatusesDataResponse> {
		const response: AxiosResponse<GetBoardStatusesDataResponse> =
			await apiNode.get<GetBoardStatusesDataResponse>(`${URL}/${id}/statuses`)
		return response.data
	},
	async createStatus({
		data,
		boardId,
	}: Omit<UpdateStatusParamsProps, 'statusId'>) {
		return await apiNode.post(`${URL}/${boardId}/statuses/create`, data)
	},
	async updateStatus({
		data,
		boardId,
		statusId,
	}: UpdateStatusParamsProps) {
		return await apiNode.patch(
			`${URL}/${boardId}/statuses/${statusId}/update`,
			data,
		)
	},
	async deleteStatus({
		boardId,
		statusId,
	}: DefaultStatusParamsProps) {
		return await apiNode.delete(
			`${URL}/${boardId}/statuses/${statusId}/delete`,
		)
	},
	async moveStatus({
		boardId,
		statusId,
		data,
	}: {
		boardId: string
		statusId: string
		data: MoveStatusDataProps
	}) {
		return await apiNode.post(
			`${URL}/${boardId}/statuses/${statusId}/move`,
			data,
		)
	},

	// cards
	async getCard({
		boardId,
		cardId,
	}: DefaultCardParamsProps): Promise<GetStatusCardDataResponse> {
		const response: AxiosResponse<GetStatusCardDataResponse> =
			await apiNode.get<GetStatusCardDataResponse>(
				`${URL}/${boardId}/cards/${cardId}`,
			)
		return response.data
	},
	async createCard({
		data,
		boardId,
	}: Omit<UpdateCardParamsProps, 'cardId'>) {
		return await apiNode.post(`${URL}/${boardId}/cards/create`, data)
	},
	async updateCard({
		data,
		boardId,
		cardId,
	}: UpdateCardParamsProps) {
		return await apiNode.patch(
			`${URL}/${boardId}/cards/${cardId}/update`,
			data,
		)
	},
	async deleteCard({
		boardId,
		cardId,
	}: DefaultCardParamsProps) {
		return await apiNode.delete(`${URL}/${boardId}/cards/${cardId}/delete`)
	},
	async moveCard({
		boardId,
		cardId,
		data,
	}: {
		boardId: string
		cardId: string
		data: MoveCardDataProps
	}) {
		return await apiNode.post(`${URL}/${boardId}/cards/${cardId}/move`, data)
	},

	// card-comments
	async getCardComments({
		boardId,
		cardId,
	}: DefaultCardParamsProps): Promise<GetCardCommentDataResponse> {
		const response: AxiosResponse<GetCardCommentDataResponse> =
			await apiNode.get<GetCardCommentDataResponse>(
				`${URL}/${boardId}/cards/${cardId}/comments`,
			)
		return {
			...response.data,
			data: (response.data?.data ?? []).map(mapCardComment),
		}
	},
	async createCardComment({
		boardId,
		cardId,
		data,
	}: {
		boardId: string
		cardId: string
		data: DefaultCardCommentDataProps
	}) {
		const response = await apiNode.post<CreateCommentPostResponse>(
			`${URL}/${boardId}/cards/${cardId}/comments`,
			data,
		)

		if (!response.data?.data) {
			return response
		}

		return {
			...response,
			data: {
				...response.data,
				data: mapCardComment(response.data.data),
			},
		}
	},
	async updateCardComment({
		data,
		boardId,
		cardId,
		commentId,
	}: UpdateCardCommentParamsProps) {
		return await apiNode.patch(
			`${URL}/${boardId}/cards/${cardId}/comments/${commentId}/update`,
			data,
		)
	},
	async deleteCardComment({
		boardId,
		cardId,
		commentId,
	}: DefaultCardCommentParamsProps) {
		return await apiNode.delete(
			`${URL}/${boardId}/cards/${cardId}/comments/${commentId}/delete`,
		)
	},
}
